// Opens the existing Shopify manager; consent is collected only by its native controls.
// Official interface: https://shopify.dev/docs/api/customer-privacy#showing-the-preferences-modal
if (!customElements.get('yd-cookie-preferences')) {
  customElements.define('yd-cookie-preferences', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      this.button.disabled = false;
      this.status.textContent = '';
      this.button.addEventListener('click', () => this.openPreferences());
    }

    waitForManager() {
      const deadline = Date.now() + 12000;
      return new Promise((resolve, reject) => {
        const check = () => {
          if (!this.isConnected) return reject(new Error('Control removed'));
          // Initial Shopify banner boot can otherwise overwrite an early open request.
          if (document.readyState === 'complete' && typeof window.privacyBanner?.showPreferences === 'function') {
            return resolve(window.privacyBanner);
          }
          if (Date.now() >= deadline) return reject(new Error('Manager unavailable'));
          window.setTimeout(check, 100);
        };
        check();
      });
    }

    waitForDialog(request) {
      const deadline = Date.now() + 6000;
      return new Promise((resolve) => {
        const check = () => {
          if (!request.active) return resolve(false);
          // The native promise can resolve before rendering, including after a network failure.
          // Observe its actual dialog; never create or change any of its content or choices.
          const dialog = document.getElementById('shopify-pc__prefs__dialog');
          if (dialog?.getClientRects().length && window.getComputedStyle(dialog).visibility !== 'hidden') {
            return resolve(true);
          }
          if (!this.isConnected || Date.now() >= deadline) return resolve(false);
          window.setTimeout(check, 100);
        };
        check();
      });
    }

    async openPreferences() {
      if (this.opening) return;
      this.opening = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Opening cookie preferences…';
      let timer;
      const request = { active: true };
      try {
        const manager = await this.waitForManager();
        await Promise.race([
          (async () => {
            await manager.showPreferences();
            if (await this.waitForDialog(request)) return;
            // One retry belongs to the same click, if initial native boot displaced the request.
            if (!request.active || !this.isConnected) throw new Error('Request ended');
            await manager.showPreferences();
            if (!(await this.waitForDialog(request))) throw new Error('Native dialog unavailable');
          })(),
          new Promise((_, reject) => {
            timer = window.setTimeout(() => reject(new Error('Manager response timed out')), 13000);
          }),
        ]);
        this.status.textContent = '';
      } catch (_) {
        this.status.textContent = 'Cookie preferences could not be opened. Please try again or reload the page.';
      } finally {
        request.active = false;
        window.clearTimeout(timer);
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.opening = false;
      }
    }
  });
}
